import React, { useState } from 'react';
import { WEAPONS, ARMOR, ITEMS } from '../data/items';
import { calculateWeaponStats } from '../utils/combatUtils';
import './Inventory.css';

const Inventory = ({ character, inventoryActions, proficiencyBonus }) => {
    const { inventory, abilityScores } = character;
    const [activeModal, setActiveModal] = useState(null); // 'weapons', 'armor', 'items' or null
    const [searchTerm, setSearchTerm] = useState('');

    const handleAddItem = (item) => {
        // Create unique ID
        const newItem = {
            ...item,
            id: `${item.name.toLowerCase().replace(/\s/g, '-')}-${Date.now()}`,
            equipped: false,
            quantity: 1 // Default for general items
        };

        let category = 'items';
        if (activeModal === 'weapons') category = 'weapons';
        if (activeModal === 'armor') category = 'armor';

        inventoryActions.addItem(category, newItem);
        setActiveModal(null);
        setSearchTerm('');
    };

    const getModalList = () => {
        let list = [];
        if (activeModal === 'weapons') list = WEAPONS;
        if (activeModal === 'armor') list = ARMOR;
        if (activeModal === 'items') list = ITEMS;

        return list.filter(i => i.name.toLowerCase().includes(searchTerm.toLowerCase()));
    };

    return (
        <div className="inventory-container">
            <h2>Inventory</h2>

            {/* Currency Bar */}
            <div className="inventory-section">
                <div className="currency-bar">
                    {['cp', 'sp', 'ep', 'gp', 'pp'].map(curr => (
                        <div key={curr} className="currency-item">
                            <label className="currency-label">{curr.toUpperCase()}</label>
                            <input
                                type="number"
                                className={`currency-input ${curr}`}
                                value={inventory.currency[curr]}
                                onChange={(e) => inventoryActions.updateCurrency(curr, e.target.value)}
                            />
                        </div>
                    ))}
                </div>
            </div>

            {/* Weapons */}
            <div className="inventory-section">
                <div className="inventory-header">
                    <h3>Weapons</h3>
                    <button className="inv-add-btn" onClick={() => setActiveModal('weapons')}>+ Add Weapon</button>
                </div>
                <div className="inventory-grid">
                    {inventory.weapons.map(w => {
                        const { attackBonus, damageMod } = calculateWeaponStats(w, abilityScores, proficiencyBonus);
                        const sign = damageMod >= 0 ? '+' : '';

                        return (
                            <div key={w.id} className={`inv-item-card ${w.equipped ? 'equipped' : ''}`}>
                                <div className="inv-item-header">
                                    <span className="inv-item-name">{w.name}</span>
                                    <span className="inv-item-type">{w.type}</span>
                                </div>
                                <div className="inv-item-stats">
                                    <div className="stat-row">
                                        <span className="stat-label">To Hit</span>
                                        <span className="stat-val">+{attackBonus}</span>
                                    </div>
                                    <div className="stat-row">
                                        <span className="stat-label">Damage</span>
                                        <span className="stat-val">{w.damage} {sign}{damageMod}</span>
                                    </div>
                                    <div className="stat-row">
                                        <span className="stat-label">Range</span>
                                        <span className="stat-val">{w.properties && w.properties.find(p => p.includes('Range')) ? w.properties.find(p => p.includes('Range')) : 'Melee'}</span>
                                    </div>
                                    <div className="stat-row">
                                        <span className="stat-label">Type</span>
                                        <span className="stat-val">{w.damageType}</span>
                                    </div>
                                </div>
                                <div className="inv-item-notes">
                                    {w.properties && w.properties.join(', ')}
                                </div>
                                <div className="inv-actions">
                                    <button className="inv-btn delete" onClick={() => inventoryActions.removeItem('weapons', w.id)}>X</button>
                                    <button
                                        className={`inv-btn equip ${w.equipped ? 'active' : ''}`}
                                        onClick={() => inventoryActions.toggleEquip('weapons', w.id)}
                                    >
                                        {w.equipped ? 'Equipped' : 'Equip'}
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Armor */}
            <div className="inventory-section">
                <div className="inventory-header">
                    <h3>Armor</h3>
                    <button className="inv-add-btn" onClick={() => setActiveModal('armor')}>+ Add Armor</button>
                </div>
                <div className="inventory-grid">
                    {inventory.armor.map(a => (
                        <div key={a.id} className={`inv-item-card ${a.equipped ? 'equipped' : ''}`}>
                            <div className="inv-item-header">
                                <span className="inv-item-name">{a.name}</span>
                                <span className="inv-item-type">{a.type}</span>
                            </div>
                            <div className="inv-item-stats">
                                <div className="stat-row">
                                    <span className="stat-label">AC Base</span>
                                    <span className="stat-val">{a.ac}</span>
                                </div>
                                {a.stealthDisadvantage && (
                                    <div className="stat-row">
                                        <span className="stat-label">Stealth</span>
                                        <span className="stat-val">Disadv</span>
                                    </div>
                                )}
                            </div>
                            <div className="inv-actions">
                                <button className="inv-btn delete" onClick={() => inventoryActions.removeItem('armor', a.id)}>X</button>
                                <button
                                    className={`inv-btn equip ${a.equipped ? 'active' : ''}`}
                                    onClick={() => inventoryActions.toggleEquip('armor', a.id)}
                                >
                                    {a.equipped ? 'Equipped' : 'Equip'}
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Items */}
            <div className="inventory-section">
                <div className="inventory-header">
                    <h3>Backpack</h3>
                    <button className="inv-add-btn" onClick={() => setActiveModal('items')}>+ Add Item</button>
                </div>
                <div className="inventory-grid">
                    {inventory.items.map(i => (
                        <div key={i.id} className="inv-item-card">
                            <div className="inv-item-header">
                                <span className="inv-item-name">{i.name}</span>
                                <span className="inv-item-type">Qty: {i.quantity}</span>
                            </div>
                            <div className="inv-item-notes">
                                {i.notes}
                            </div>
                            <div className="inv-actions">
                                <button className="inv-btn delete" onClick={() => inventoryActions.removeItem('items', i.id)}>X</button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Add Item Modal */}
            {activeModal && (
                <div className="modal-overlay" onClick={() => setActiveModal(null)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3 className="modal-title">Add {activeModal.charAt(0).toUpperCase() + activeModal.slice(1)}</h3>
                            <button className="modal-close" onClick={() => setActiveModal(null)}>&times;</button>
                        </div>
                        <input
                            type="text"
                            className="modal-search"
                            placeholder="Search..."
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            autoFocus
                        />
                        <div className="modal-list">
                            {getModalList().map((item, idx) => (
                                <div key={idx} className="modal-item" onClick={() => handleAddItem(item)}>
                                    <span className="modal-item-name">{item.name}</span>
                                    <span className="modal-item-meta">
                                        {item.type && `${item.type} `}
                                        {item.damage && `(${item.damage})`}
                                        {item.ac && `(AC ${item.ac})`}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Inventory;
